import { FileAttachment } from '~/modules/enquiry/component/FileAttachment';

/** Per-file size cap (bytes). Tighter than the default enquiry cap because
 *  each Get Matched email rolls all attachments into Mandrill's 25MB total. */
const MAX_FILE_SIZE = 4 * 1024 * 1024;
const MAX_ATTACHMENTS = 5;

const MAX_DESCRIPTION_LENGTH = 5000;

interface Step6ProjectValue {
    description: string;
    professionals: string | null;
    startDate: string | null;
    endDate: string | null;
    attachments: File[];
}

interface Step6ProjectProps {
    title: string;
    /** Label + options for the "Which X are you looking for" select. Null hides the field entirely. */
    lookingForField: {
        label: string;
        options: string[];
    } | null;
    /** Project flow drops the timeline question. */
    showTimeline: boolean;
    descriptionLabel: string;
    descriptionPlaceholder: string;
    timelineLabel: string;
    timelineStartLabel: string;
    timelineEndLabel: string;
    value: Step6ProjectValue;
    onChange: (next: Step6ProjectValue) => void;
    /**
     * Optional inline notice shown above the file-upload control. Used by the
     * OAuth-resume flow to explain that previously-attached files were lost
     * across the redirect and need to be re-attached.
     */
    attachmentNotice?: string;
}

export const Step6Project = ({
    title,
    lookingForField,
    showTimeline,
    descriptionLabel,
    descriptionPlaceholder,
    timelineLabel,
    timelineStartLabel,
    timelineEndLabel,
    value,
    onChange,
    attachmentNotice,
}: Step6ProjectProps) => {
    return (
        <div className="flex flex-col gap-[1.375rem]">
            <h2 className="text-[1.3125rem] font-medium uppercase leading-[1.1] tracking-[-0.02em] text-[#161616]">
                {title}
            </h2>
            <div className="flex flex-col gap-7">
                <Field label={descriptionLabel}>
                    <textarea
                        value={value.description}
                        onChange={(e) =>
                            onChange({ ...value, description: e.target.value })
                        }
                        placeholder={descriptionPlaceholder}
                        rows={8}
                        maxLength={MAX_DESCRIPTION_LENGTH}
                        className="w-full resize-none rounded-[0.75rem] bg-[#fdfdfd] px-3 py-4 text-[0.875rem] leading-[1.2] tracking-[-0.02em] text-black placeholder:text-black/30 focus:outline-none"
                    />
                    <DescriptionCounter length={value.description.length} />
                </Field>
                {lookingForField && (
                    <Field label={lookingForField.label}>
                        <SelectControl
                            value={value.professionals ?? ''}
                            options={lookingForField.options}
                            placeholder="Select"
                            onChange={(next) =>
                                onChange({ ...value, professionals: next })
                            }
                        />
                    </Field>
                )}
                {showTimeline && (
                    <Field label={timelineLabel}>
                        <ProjectTimeline
                            startDate={value.startDate}
                            endDate={value.endDate}
                            startLabel={timelineStartLabel}
                            endLabel={timelineEndLabel}
                            onChange={({ startDate, endDate }) =>
                                onChange({ ...value, startDate, endDate })
                            }
                        />
                    </Field>
                )}
                <Field
                    label={`Attach files (max ${MAX_ATTACHMENTS}, ${MAX_FILE_SIZE / (1024 * 1024)}MB each)`}
                >
                    {attachmentNotice && (
                        <span className="rounded-[0.5rem] bg-[#fff4e0] px-3 py-2 text-[0.75rem] leading-[1.3] tracking-[-0.01em] text-[#a35a00]">
                            {attachmentNotice}
                        </span>
                    )}
                    <FileAttachment
                        files={value.attachments}
                        maxAttachments={MAX_ATTACHMENTS}
                        maxFileSize={MAX_FILE_SIZE}
                        onChange={(attachments) =>
                            onChange({ ...value, attachments })
                        }
                    />
                </Field>
            </div>
        </div>
    );
};

const Field = ({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) => (
    <div className="flex flex-col gap-2">
        <span className="text-[0.78rem] font-medium uppercase leading-[1.1] tracking-[-0.02em] text-[#161616]">
            {label}
        </span>
        {children}
    </div>
);

const DescriptionCounter = ({ length }: { length: number }) => {
    const ratio = length / MAX_DESCRIPTION_LENGTH;
    const tone =
        ratio >= 1
            ? 'text-red-600'
            : ratio >= 0.9
              ? 'text-[#a35a00]'
              : 'text-[#858585]';
    return (
        <span className={`self-end text-[0.7rem] tracking-[-0.01em] ${tone}`}>
            {length} / {MAX_DESCRIPTION_LENGTH}
        </span>
    );
};

/**
 * Date-range picker matching BuildHub's ProjectTimelineStep shape. "I'm not
 * sure yet" nulls both dates so the brief can be submitted without committing
 * to a timeline.
 */
const ProjectTimeline = ({
    startDate,
    endDate,
    startLabel,
    endLabel,
    onChange,
}: {
    startDate: string | null;
    endDate: string | null;
    startLabel: string;
    endLabel: string;
    onChange: (next: {
        startDate: string | null;
        endDate: string | null;
    }) => void;
}) => {
    const notSure = startDate === null && endDate === null;
    return (
        <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2">
                <DateInput
                    label={startLabel}
                    value={startDate ?? ''}
                    onChange={(next) =>
                        onChange({ startDate: next || null, endDate })
                    }
                />
                <DateInput
                    label={endLabel}
                    value={endDate ?? ''}
                    onChange={(next) =>
                        onChange({ startDate, endDate: next || null })
                    }
                />
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-[0.75rem] tracking-[-0.01em] text-[#161616]">
                <input
                    type="checkbox"
                    checked={notSure}
                    onChange={(e) => {
                        if (e.target.checked) {
                            onChange({ startDate: null, endDate: null });
                        }
                    }}
                />
                I&apos;m not sure yet
            </label>
        </div>
    );
};

const DateInput = ({
    label,
    value,
    onChange,
}: {
    label: string;
    value: string;
    onChange: (next: string) => void;
}) => (
    <label className="flex flex-col gap-1">
        <span className="text-[0.7rem] font-medium tracking-[-0.01em] text-[#858585]">
            {label}
        </span>
        <input
            type="date"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="rounded-[0.75rem] bg-[#fdfdfd] px-3 py-3 text-[0.875rem] tracking-[-0.02em] text-black focus:outline-none"
        />
    </label>
);

const SelectControl = ({
    value,
    options,
    placeholder,
    onChange,
}: {
    value: string;
    options: string[];
    placeholder: string;
    onChange: (next: string) => void;
}) => (
    <div className="relative">
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full appearance-none rounded-[0.75rem] bg-[#fdfdfd] px-3 py-4 pr-9 text-[0.875rem] tracking-[-0.02em] text-black focus:outline-none"
        >
            <option value="" disabled>
                {placeholder}
            </option>
            {options.map((opt) => (
                <option key={opt} value={opt}>
                    {opt}
                </option>
            ))}
        </select>
        <span
            aria-hidden
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[0.7rem] text-black"
        >
            ▾
        </span>
    </div>
);
